import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { navigate, useLocation } from '../../lib/router';
import {
  AsyncButton, Badge, Button, Card, Checkbox, Empty, ErrorBox, Field, Input, Loading, PageHeader, Select, Textarea, useToast,
} from '../../ui/components';

interface FieldDef { key: string; type: string; options?: string[]; fields?: FieldDef[]; max?: number }
interface SectionDef { type: string; live: boolean; fields: FieldDef[] }

const toBase64 = (f: File): Promise<string> => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result));
  r.onerror = () => reject(r.error);
  r.readAsDataURL(f);
});

/** Website: start from a design, see the pages and their state, and put the site live. */
export function WebsitePage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const site = useApi(() => api<any>(`${T(gym.slug)}/admin/site`), []);
  const editing = query.get('page');

  if (site.error) return <div className="page"><ErrorBox error={site.error} onRetry={site.reload} /></div>;
  if (!site.data) return <Loading />;
  if (editing) return <PageEditor id={editing} sections={site.data.sections} pages={site.data.pages} onDone={() => { navigate(`${gym.base}/admin/website`); site.reload(); }} />;

  const { settings, pages, included, address } = site.data;
  const unpublished = pages.filter((p: any) => p.hasUnpublishedChanges).length;

  if (!pages.length) {
    return (
      <div className="page">
        <PageHeader title={i18n.t('siteedit.title')} subtitle={i18n.t('siteedit.start_subtitle')} />
        {!included && <div className="alert alert-warn">{i18n.t('siteedit.not_included')}</div>}
        <div className="site-themes">
          {(['studio', 'box', 'coach'] as const).map((theme) => (
            <Card key={theme} className={`site-theme site-theme-${theme}`}>
              <div className="site-theme-sample" aria-hidden="true"><span /><span /><span /></div>
              <h3>{i18n.t(`siteedit.theme_${theme}`)}</h3>
              <p className="small muted">{i18n.t(`siteedit.theme_${theme}_text`)}</p>
              <AsyncButton onClick={async () => {
                await api(`${T(gym.slug)}/admin/site/template`, { body: { theme } });
                toast.ok(i18n.t('siteedit.created'));
                site.reload();
              }}>{i18n.t('siteedit.use_theme')}</AsyncButton>
            </Card>
          ))}
        </div>
        <p className="small muted">{i18n.t('siteedit.start_note')}</p>
      </div>
    );
  }

  const move = async (index: number, dir: -1 | 1): Promise<void> => {
    const ids = pages.map((p: any) => p.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    await api(`${T(gym.slug)}/admin/site/order`, { method: 'PUT', body: { ids } });
    site.reload();
  };

  return (
    <div className="page">
      <PageHeader title={i18n.t('siteedit.title')} actions={
        <a className="btn btn-secondary" href={settings.enabled ? address : `/site/${gym.slug}`} target="_blank" rel="noreferrer">{i18n.t('siteedit.view_site')}</a>
      } />
      {!included && <div className="alert alert-warn">{i18n.t('siteedit.not_included')}</div>}

      <Card>
        <div className="row between wrap">
          <div>
            <Badge value={settings.enabled ? 'good' : 'neutral'} label={i18n.t(settings.enabled ? 'siteedit.live' : 'siteedit.offline')} />
            <div className="small muted" style={{ marginTop: 6 }}>{settings.enabled ? address : i18n.t('siteedit.offline_hint')}</div>
          </div>
          <div className="row gap-sm wrap">
            {unpublished > 0 && (
              <AsyncButton variant="secondary" onClick={async () => {
                await api(`${T(gym.slug)}/admin/site/publish-all`, { body: {} });
                toast.ok(i18n.t('siteedit.published_all'));
                site.reload();
              }}>{i18n.t('siteedit.publish_all', { count: unpublished })}</AsyncButton>
            )}
            <AsyncButton disabled={!included && !settings.enabled} onClick={async () => {
              await api(`${T(gym.slug)}/admin/site/settings`, { method: 'PUT', body: { enabled: !settings.enabled } });
              toast.ok(i18n.t(settings.enabled ? 'siteedit.taken_offline' : 'siteedit.now_live'));
              site.reload();
            }}>{i18n.t(settings.enabled ? 'siteedit.take_offline' : 'siteedit.go_live')}</AsyncButton>
          </div>
        </div>
      </Card>

      <Card title={i18n.t('siteedit.pages')} actions={
        <AsyncButton size="sm" variant="secondary" onClick={async () => {
          const title = window.prompt(i18n.t('siteedit.new_page_prompt'));
          if (!title) return;
          const p = await api(`${T(gym.slug)}/admin/site/pages`, { body: { title, sections: [{ type: 'text', heading: title }] } });
          navigate(`${gym.base}/admin/website?page=${p.id}`);
        }}>+ {i18n.t('siteedit.add_page')}</AsyncButton>
      }>
        <div className="site-pages">
          {pages.map((p: any, i: number) => (
            <div key={p.id} className="row between wrap site-page-row">
              <div>
                <strong>{p.title}</strong> <span className="small muted">/{p.slug === 'home' ? '' : p.slug}</span>
                <div className="small muted">
                  {p.publishedAt ? (p.hasUnpublishedChanges ? i18n.t('siteedit.changes_waiting') : i18n.t('siteedit.published')) : i18n.t('siteedit.draft')}
                  {!p.showInNav && p.slug !== 'home' ? ` · ${i18n.t('siteedit.hidden_from_menu')}` : ''}
                </div>
              </div>
              <div className="row gap-sm">
                <Button size="sm" variant="ghost" aria-label={i18n.t('siteedit.move_up')} disabled={i === 0} onClick={() => move(i, -1)}>↑</Button>
                <Button size="sm" variant="ghost" aria-label={i18n.t('siteedit.move_down')} disabled={i === pages.length - 1} onClick={() => move(i, 1)}>↓</Button>
                <Button size="sm" onClick={() => navigate(`${gym.base}/admin/website?page=${p.id}`)}>{i18n.t('siteedit.edit')}</Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <SiteSettingsCard settings={settings} onSaved={site.reload} />

      <Card title={i18n.t('siteedit.start_over')}>
        <p className="small muted">{i18n.t('siteedit.start_over_text')}</p>
        <div className="row gap-sm wrap">
          {(['studio', 'box', 'coach'] as const).map((theme) => (
            <AsyncButton key={theme} size="sm" variant="secondary" confirm={i18n.t('siteedit.start_over_confirm')} onClick={async () => {
              await api(`${T(gym.slug)}/admin/site/template`, { body: { theme, replace: true } });
              toast.ok(i18n.t('siteedit.created'));
              site.reload();
            }}>{i18n.t(`siteedit.theme_${theme}`)}</AsyncButton>
          ))}
        </div>
      </Card>
    </div>
  );
}

function SiteSettingsCard({ settings, onSaved }: { settings: any; onSaved(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [v, setV] = useState({ ...settings, social: { ...(settings.social ?? {}) }, announcement: { ...(settings.announcement ?? {}) } });
  return (
    <Card title={i18n.t('siteedit.settings')}>
      <div className="grid-2">
        <Field label={i18n.t('siteedit.design')} hint={i18n.t('siteedit.design_hint')}>
          <Select value={v.theme} onChange={(e) => setV({ ...v, theme: e.target.value })}
            options={['studio', 'box', 'coach'].map((x) => ({ value: x, label: i18n.t(`siteedit.theme_${x}`) }))} />
        </Field>
        <Field label={i18n.t('siteedit.tagline')} hint={i18n.t('siteedit.tagline_hint')}>
          <Input value={v.tagline} maxLength={160} onChange={(e) => setV({ ...v, tagline: e.target.value })} />
        </Field>
        <Field label={i18n.t('siteedit.announcement')} hint={i18n.t('siteedit.announcement_hint')}>
          <Input value={v.announcement.text ?? ''} onChange={(e) => setV({ ...v, announcement: { ...v.announcement, text: e.target.value } })} />
        </Field>
        <Field label={i18n.t('siteedit.announcement_link')}>
          <Input value={v.announcement.link ?? ''} placeholder="https://" onChange={(e) => setV({ ...v, announcement: { ...v.announcement, link: e.target.value } })} />
        </Field>
        {(['instagram', 'facebook', 'tiktok', 'youtube', 'linkedin'] as const).map((k) => (
          <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
            <Input value={v.social[k] ?? ''} placeholder="https://" onChange={(e) => setV({ ...v, social: { ...v.social, [k]: e.target.value } })} />
          </Field>
        ))}
      </div>
      <Field label={i18n.t('siteedit.footer_text')}>
        <Textarea rows={2} value={v.footerText} maxLength={400} onChange={(e) => setV({ ...v, footerText: e.target.value })} />
      </Field>
      <div className="row between wrap">
        <a className="small" href={`${gym.base}/admin/settings?tab=branding`}>{i18n.t('siteedit.branding_link')}</a>
        <AsyncButton onClick={async () => {
          await api(`${T(gym.slug)}/admin/site/settings`, { method: 'PUT', body: { theme: v.theme, tagline: v.tagline, footerText: v.footerText, social: v.social, announcement: v.announcement } });
          toast.ok(i18n.t('common.saved'));
          onSaved();
        }}>{i18n.t('common.save')}</AsyncButton>
      </div>
    </Card>
  );
}

/** One page: its sections on the left, the draft as visitors would see it on the right. */
function PageEditor({ id, sections: defs, pages, onDone }: { id: string; sections: SectionDef[]; pages: any[]; onDone(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [page, setPage] = useState<any | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [stamp, setStamp] = useState(0);
  const [dirty, setDirty] = useState(false);
  const lists = useApi(async () => ({
    lesson_types: (await api<any>(`${T(gym.slug)}/admin/r/lesson_types`, { query: { pageSize: 100 } })).items ?? [],
    locations: (await api<any>(`${T(gym.slug)}/admin/r/locations`, { query: { pageSize: 50 } })).items ?? [],
  }), []);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    void api<any>(`${T(gym.slug)}/admin/site/pages/${id}`).then(setPage);
    void api<any>(`${T(gym.slug)}/admin/site/preview-token`).then((r) => setToken(r.token));
  }, [id]);

  if (!page) return <Loading />;
  const set = (patch: any): void => { setPage({ ...page, ...patch }); setDirty(true); };
  const setSection = (sid: string, patch: any): void => set({ sections: page.sections.map((s: any) => (s.id === sid ? { ...s, ...patch } : s)) });
  const moveSection = (index: number, dir: -1 | 1): void => {
    const list = [...page.sections];
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    [list[index], list[j]] = [list[j], list[index]];
    set({ sections: list });
  };
  const save = async (): Promise<any> => {
    const saved = await api(`${T(gym.slug)}/admin/site/pages/${id}`, {
      method: 'PUT',
      body: { title: page.title, slug: page.slug, navLabel: page.navLabel, showInNav: page.showInNav, seoTitle: page.seoTitle, seoDescription: page.seoDescription, sections: page.sections },
    });
    setPage(saved);
    setDirty(false);
    setStamp(Date.now());
    return saved;
  };
  const previewSrc = token ? `/site/${gym.slug}${page.slug === 'home' ? '' : `/${page.slug}`}?preview=${encodeURIComponent(token)}&t=${stamp}` : '';

  return (
    <div className="page site-editor-page">
      <PageHeader title={page.title} subtitle={`/${page.slug === 'home' ? '' : page.slug}`} actions={
        <div className="row gap-sm">
          <Button variant="ghost" onClick={() => { if (!dirty || window.confirm(i18n.t('siteedit.leave_unsaved'))) onDone(); }}>{i18n.t('siteedit.back')}</Button>
          <AsyncButton variant="secondary" disabled={!dirty} onClick={async () => { await save(); toast.ok(i18n.t('siteedit.saved_draft')); }}>{i18n.t('siteedit.save_draft')}</AsyncButton>
          <AsyncButton onClick={async () => {
            await save();
            setPage(await api(`${T(gym.slug)}/admin/site/pages/${id}/publish`, { body: {} }));
            toast.ok(i18n.t('siteedit.published_page'));
          }}>{i18n.t('siteedit.publish')}</AsyncButton>
        </div>
      } />
      <div className="site-editor">
        <div className="stack">
          {page.sections.length === 0 && <Empty icon="▢">{i18n.t('siteedit.no_sections')}</Empty>}
          {page.sections.map((s: any, index: number) => {
            const def = defs.find((d) => d.type === s.type);
            const isOpen = open === s.id;
            return (
              <Card key={s.id} className="site-section">
                <div className="row between">
                  <button type="button" className="link site-section-title" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : s.id)}>
                    <strong>{i18n.t(`siteedit.section_${s.type}`)}</strong>
                    {s.heading ? <span className="small muted"> · {s.heading}</span> : null}
                    {def?.live ? <Badge value="info" label={i18n.t('siteedit.live_data')} /> : null}
                  </button>
                  <div className="row gap-sm">
                    <Button size="sm" variant="ghost" aria-label={i18n.t('siteedit.move_up')} disabled={index === 0} onClick={() => moveSection(index, -1)}>↑</Button>
                    <Button size="sm" variant="ghost" aria-label={i18n.t('siteedit.move_down')} disabled={index === page.sections.length - 1} onClick={() => moveSection(index, 1)}>↓</Button>
                    <Button size="sm" variant="ghost" aria-label={i18n.t('siteedit.remove')} onClick={() => { if (window.confirm(i18n.t('siteedit.remove_confirm'))) set({ sections: page.sections.filter((x: any) => x.id !== s.id) }); }}>✕</Button>
                  </div>
                </div>
                {isOpen && def && (
                  <div className="stack-sm" style={{ marginTop: 12 }}>
                    {def.live && <p className="small muted">{i18n.t(`siteedit.live_${s.type}`)}</p>}
                    {def.fields.map((f) => (
                      <FieldInput key={f.key} def={f} value={s[f.key]} lists={lists.data} pages={pages}
                        onChange={(value) => setSection(s.id, { [f.key]: value })} />
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
          <Card>
            <Field label={i18n.t('siteedit.add_section')}>
              <Select value="" placeholder={i18n.t('siteedit.choose_section')} onChange={(e) => {
                if (!e.target.value) return;
                const sid = Math.random().toString(36).slice(2, 10);
                set({ sections: [...page.sections, { id: sid, type: e.target.value }] });
                setOpen(sid);
              }} options={defs.map((d) => ({ value: d.type, label: i18n.t(`siteedit.section_${d.type}`) }))} />
            </Field>
          </Card>
          <Card title={i18n.t('siteedit.page_settings')}>
            <div className="grid-2">
              <Field label={i18n.t('siteedit.page_title')}><Input value={page.title} onChange={(e) => set({ title: e.target.value })} /></Field>
              <Field label={i18n.t('siteedit.page_address')} hint={page.slug === 'home' ? i18n.t('siteedit.home_address') : undefined}>
                <Input value={page.slug} disabled={page.slug === 'home'} onChange={(e) => set({ slug: e.target.value })} />
              </Field>
              <Field label={i18n.t('siteedit.menu_label')}><Input value={page.navLabel ?? ''} onChange={(e) => set({ navLabel: e.target.value })} /></Field>
              <Field label={i18n.t('siteedit.seo_title')} hint={i18n.t('siteedit.seo_title_hint')}><Input value={page.seoTitle ?? ''} maxLength={70} onChange={(e) => set({ seoTitle: e.target.value })} /></Field>
            </div>
            <Field label={i18n.t('siteedit.seo_description')} hint={i18n.t('siteedit.seo_description_hint')}>
              <Textarea rows={2} value={page.seoDescription ?? ''} maxLength={170} onChange={(e) => set({ seoDescription: e.target.value })} />
            </Field>
            {page.slug !== 'home' && <Checkbox label={i18n.t('siteedit.show_in_menu')} checked={page.showInNav} onChange={(c) => set({ showInNav: c })} />}
            {page.slug !== 'home' && (
              <AsyncButton size="sm" variant="danger" confirm={i18n.t('siteedit.delete_confirm')} onClick={async () => {
                await api(`${T(gym.slug)}/admin/site/pages/${id}`, { method: 'DELETE' });
                onDone();
              }}>{i18n.t('siteedit.delete_page')}</AsyncButton>
            )}
          </Card>
        </div>
        <div className="site-preview">
          <div className="row between small muted"><span>{i18n.t('siteedit.preview')}</span>{dirty && <span>{i18n.t('siteedit.preview_after_save')}</span>}</div>
          {previewSrc ? <iframe ref={frame} title={i18n.t('siteedit.preview')} src={previewSrc} /> : <Loading />}
        </div>
      </div>
    </div>
  );
}

/** One field of a section, built from its definition. */
function FieldInput({ def, value, onChange, lists, pages }: { def: FieldDef; value: any; onChange(v: any): void; lists: any; pages: any[] }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const label = i18n.t(`siteedit.field_${def.key}`);
  if (def.type === 'toggle') return <Checkbox label={label} checked={value !== false} onChange={onChange} />;
  if (def.type === 'textarea' || def.type === 'markdown') {
    return <Field label={label} hint={def.type === 'markdown' ? i18n.t('siteedit.markdown_hint') : undefined}><Textarea rows={def.type === 'markdown' ? 6 : 3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} /></Field>;
  }
  if (def.type === 'number') return <Field label={label}><Input type="number" min={1} max={50} value={value ?? ''} onChange={(e) => onChange(Number(e.target.value))} /></Field>;
  if (def.type === 'select') {
    const dynamic = def.options?.[0] === 'lesson_types' || def.options?.[0] === 'locations';
    const options = dynamic
      ? (lists?.[def.options![0]] ?? []).map((x: any) => ({ value: x.id, label: x.name }))
      : (def.options ?? []).map((o) => ({ value: o, label: /^\d+$/.test(o) ? i18n.t('siteedit.days', { count: Number(o) }) : i18n.t(`siteedit.option_${o}`) }));
    return <Field label={label}><Select value={value ?? ''} placeholder={dynamic ? i18n.t('siteedit.all') : undefined} onChange={(e) => onChange(e.target.value || undefined)} options={options} /></Field>;
  }
  if (def.type === 'image') {
    return (
      <Field label={label}>
        <div className="row gap-sm wrap">
          {value && <img src={value} alt="" className="site-thumb" />}
          <input type="file" accept="image/jpeg,image/png,image/webp" aria-label={label} onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            try {
              const r = await api(`${T(gym.slug)}/admin/uploads`, { body: { contentType: f.type, dataBase64: await toBase64(f), visibility: 'public' } });
              onChange(r.url);
            } catch (err) { toast.error(err); }
          }} />
          {value && <Button size="sm" variant="ghost" onClick={() => onChange(undefined)}>{i18n.t('siteedit.remove_image')}</Button>}
        </div>
      </Field>
    );
  }
  if (def.type === 'items') {
    const items: any[] = Array.isArray(value) ? value : [];
    return (
      <Field label={label}>
        <div className="stack-sm">
          {items.map((item, i) => (
            <div key={i} className="site-item">
              {(def.fields ?? []).map((sub) => (
                <FieldInput key={sub.key} def={sub} value={item[sub.key]} lists={lists} pages={pages}
                  onChange={(v) => onChange(items.map((x, j) => (j === i ? { ...x, [sub.key]: v } : x)))} />
              ))}
              <Button size="sm" variant="ghost" onClick={() => onChange(items.filter((_, j) => j !== i))}>{i18n.t('siteedit.remove_item')}</Button>
            </div>
          ))}
          {items.length < (def.max ?? 20) && <Button size="sm" variant="secondary" onClick={() => onChange([...items, {}])}>+ {i18n.t('siteedit.add_item')}</Button>}
        </div>
      </Field>
    );
  }
  // a link field next to an action of "page" offers the site's own pages
  if (def.key.endsWith('Link')) {
    return <Field label={label} hint={i18n.t('siteedit.link_hint', { pages: pages.map((p) => p.slug).filter((s) => s !== 'home').join(', ') })}><Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} /></Field>;
  }
  return <Field label={label}><Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} /></Field>;
}
